import { useState, useCallback, useMemo } from 'react';
import { Building2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, fmtAmtZero, fmtPct,
  Delta, Sparkline, Section, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import type {
  CostCentrePerformanceDto, CostCentrePerformanceFilter, CostCentrePerformanceRow,
} from '@/types/analytics.types';

const POSITIVE_BAR_COLOUR = '#10b981';
const NEGATIVE_BAR_COLOUR = '#ef4444';

// ── Chart tooltip ─────────────────────────────────────────────────────────────

const AmountTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs">
      {label && <p className="font-semibold text-gray-700 dark:text-gray-200 mb-2">{label}</p>}
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-gray-900 dark:text-white tabular-nums">
            {fmtAmtZero(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Heat-map cell colouring ───────────────────────────────────────────────────

/**
 * Green→red band chosen from the value's sign and its magnitude relative to the
 * largest absolute value in the grid. Deliberately coarse: five bands read faster
 * than a continuous gradient in a dense table.
 */
function heatCellClass(value: number | undefined, maxAbs: number): string {
  if (value === undefined) return 'bg-gray-50 dark:bg-gray-800/40 text-gray-400 dark:text-gray-600';
  if (maxAbs <= 0 || value === 0) return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400';

  const intensity = Math.min(Math.abs(value) / maxAbs, 1);

  if (value > 0) {
    if (intensity > 0.66) return 'bg-green-500/80 text-white';
    if (intensity > 0.33) return 'bg-green-300/70 text-green-900 dark:text-green-950';
    return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300';
  }
  if (intensity > 0.66) return 'bg-red-500/80 text-white';
  if (intensity > 0.33) return 'bg-red-300/70 text-red-900 dark:text-red-950';
  return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300';
}

/** Compact heat-map label — full precision lives in the cell title attribute. */
function heatLabel(value: number | undefined): string {
  if (value === undefined) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
}

// ── Excel export ──────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Rank', header: 'Rank', width: 8 },
  { key: 'Code', header: 'Code', width: 16 },
  { key: 'Name', header: 'Name', width: 34 },
  { key: 'Revenue', header: 'Revenue', width: 16, numFmt: '#,##0.00' },
  { key: 'Expenses', header: 'Expenses', width: 16, numFmt: '#,##0.00' },
  { key: 'Profit', header: 'Profit', width: 16, numFmt: '#,##0.00' },
  { key: 'Profit Margin%', header: 'Profit Margin %', width: 15 },
  { key: 'Previous Profit', header: 'Previous Profit', width: 16, numFmt: '#,##0.00' },
  { key: 'Growth%', header: 'Growth %', width: 12 },
  { key: 'Contribution%', header: 'Contribution %', width: 14 },
];

function toExcelRow(r: CostCentrePerformanceRow): Record<string, string | number> {
  return {
    Rank: r.rank,
    Code: r.code,
    Name: r.name,
    Revenue: r.revenue,
    Expenses: r.expenses,
    Profit: r.profit,
    'Profit Margin%': r.profitMarginPercent,
    'Previous Profit': r.previousProfit,
    'Growth%': r.growthPercent ?? '',
    'Contribution%': r.contributionPercent,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CostCentrePerformancePage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<CostCentrePerformanceFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
  });
  const [data, setData] = useState<CostCentrePerformanceDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getCostCentrePerformance(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const byProfitDesc = useMemo(
    () => (data ? [...data.rows].sort((a, b) => b.profit - a.profit) : []),
    [data],
  );

  const topPerformers = useMemo(
    () => byProfitDesc.slice(0, 10).map(r => ({ name: r.name, profit: r.profit })),
    [byProfitDesc],
  );

  const bottomPerformers = useMemo(
    () => byProfitDesc.slice(-10).reverse().map(r => ({ name: r.name, profit: r.profit })),
    [byProfitDesc],
  );

  /** Largest absolute monthly value across the whole grid — the heat-map scale. */
  const heatMaxAbs = useMemo(() => {
    if (!data) return 0;
    let max = 0;
    for (const row of data.rows) {
      for (const v of row.trend) {
        const abs = Math.abs(v);
        if (abs > max) max = abs;
      }
    }
    return max;
  }, [data]);

  if (!hasPermission(Permissions.RPT_COST_CENTRE_PERF)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    const rows: Record<string, string | number>[] = data.rows.map(toExcelRow);
    rows.push({
      Rank: '',
      Code: '',
      Name: 'TOTAL',
      Revenue: data.totalRevenue,
      Expenses: data.totalExpenses,
      Profit: data.totalProfit,
      'Profit Margin%': '',
      'Previous Profit': '',
      'Growth%': '',
      'Contribution%': '',
    });
    exportToExcel({
      sheetName: 'Cost Centre Performance',
      columns: EXCEL_COLUMNS,
      rows,
      title: 'Cost Centre Performance',
      subtitle: `${data.companyName} · ${filter.fromDate} to ${filter.toDate}`,
    }, `CostCentrePerformance_${filter.fromDate}_${filter.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Cost Centre Performance</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <RunReportButton
            onClick={() => { void run(); }}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to generate the Cost Centre Performance report.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* League table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Cost Centre Performance — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                {' · '}{data.rows.length} cost centres
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>

            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1150px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_R_CLS, 'w-16')}>Rank</th>
                    <th className={cn(TH_CLS, 'w-28')}>Code</th>
                    <th className={TH_CLS}>Name</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Revenue</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Expenses</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Profit</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Profit Margin%</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Previous Profit</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Growth%</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Contribution%</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No cost centres match the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    data.rows.map((r, i) => (
                      <tr
                        key={r.costCentreId ?? `${r.code}-${i}`}
                        className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <td className={cn(AMT_CLS, 'font-semibold text-gray-500 dark:text-gray-400')}>{r.rank}</td>
                        <td className="px-3 py-2 text-sm font-mono text-gray-600 dark:text-gray-400">{r.code}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{r.name}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.revenue)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.expenses)}</td>
                        <td className={cn(
                          AMT_CLS, 'font-medium',
                          r.profit < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100',
                        )}>
                          {fmtAmt(r.profit)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtPct(r.profitMarginPercent)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtAmt(r.previousProfit)}</td>
                        <td className="px-3 py-2 text-right">
                          <Delta percent={r.growthPercent} />
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtPct(r.contributionPercent)}</td>
                        <td className="px-3 py-2 text-right">
                          <Sparkline values={r.trend} higherIsBetter />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                    <td colSpan={3} className="px-3 py-2 text-sm font-bold text-gray-900 dark:text-white">TOTAL</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.totalRevenue)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.totalExpenses)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.totalProfit)}</td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Top / bottom performers */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Section title="Top 10 Cost Centres" subtitle="Highest profit for the period">
              {topPerformers.length === 0 ? (
                <EmptyState message="No cost centres for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, topPerformers.length * 30)}>
                  <BarChart data={topPerformers} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                    <ChartTooltip content={<AmountTooltip />} />
                    <Bar dataKey="profit" name="Profit" radius={[0, 3, 3, 0]}>
                      {topPerformers.map((d, i) => (
                        <Cell key={`${d.name}-${i}`} fill={d.profit < 0 ? NEGATIVE_BAR_COLOUR : POSITIVE_BAR_COLOUR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Section title="Bottom 10 Cost Centres" subtitle="Lowest profit for the period">
              {bottomPerformers.length === 0 ? (
                <EmptyState message="No cost centres for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, bottomPerformers.length * 30)}>
                  <BarChart data={bottomPerformers} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                    <ChartTooltip content={<AmountTooltip />} />
                    <Bar dataKey="profit" name="Profit" radius={[0, 3, 3, 0]}>
                      {bottomPerformers.map((d, i) => (
                        <Cell key={`${d.name}-${i}`} fill={d.profit < 0 ? NEGATIVE_BAR_COLOUR : POSITIVE_BAR_COLOUR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>
          </div>

          {/* Heat map */}
          <Section
            title="Monthly Profit Heat Map"
            subtitle="Cost centre by month — green is profit, red is loss"
          >
            {data.rows.length === 0 || data.monthLabels.length === 0 ? (
              <EmptyState message="No monthly data for this period." />
            ) : (
              <div className="overflow-auto">
                <table className="border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-white dark:bg-gray-900 px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Cost Centre
                      </th>
                      {data.monthLabels.map(m => (
                        <th
                          key={m}
                          className="px-2 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap"
                        >
                          {m}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={r.costCentreId ?? `${r.code}-${i}`}>
                        <td className="sticky left-0 bg-white dark:bg-gray-900 px-3 py-1 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap border-r border-gray-100 dark:border-gray-800">
                          <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-2">{r.code}</span>
                          {r.name}
                        </td>
                        {data.monthLabels.map((m, idx) => {
                          const value = r.trend[idx];
                          return (
                            <td key={m} className="p-0.5">
                              <div
                                className={cn(
                                  'px-2 py-1 rounded text-center tabular-nums font-medium min-w-[52px]',
                                  heatCellClass(value, heatMaxAbs),
                                )}
                                title={value === undefined ? `${m}: no data` : `${r.name} · ${m}: ${fmtAmtZero(value)}`}
                              >
                                {heatLabel(value)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Cost Centre Performance"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Cost Centre Performance"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period', value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Cost Centres', value: String(data.rows.length) },
              { label: 'Total Profit', value: fmtAmtZero(data.totalProfit) },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Rank', 'Code', 'Name', 'Revenue', 'Expenses', 'Profit', 'Margin%', 'Prev. Profit', 'Contribution%'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i === 1 || i === 2 ? 'left' : 'right', fontWeight: 700,
                      whiteSpace: 'nowrap', fontSize: '8.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.costCentreId ?? `${r.code}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>{r.rank}</td>
                  <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#6b7280' }}>{r.code}</td>
                  <td style={{ padding: '3px 6px' }}>{r.name}</td>
                  {[r.revenue, r.expenses, r.profit].map((v, idx) => (
                    <td key={idx} style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: idx === 2 ? 600 : 400 }}>{fmtAmt(v)}</td>
                  ))}
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtPct(r.profitMarginPercent)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(r.previousProfit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtPct(r.contributionPercent)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #1e3a5f', background: '#f9fafb' }}>
                <td colSpan={3} style={{ padding: '4px 6px', fontWeight: 700 }}>TOTAL</td>
                {[data.totalRevenue, data.totalExpenses, data.totalProfit].map((v, idx) => (
                  <td key={idx} style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(v)}</td>
                ))}
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
